import React, { useEffect } from "react";
import { Copy, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { cn } from "@/modern/utils";
import { SURFACE_ICON_BTN_CLS } from "@/react/components/entityViews/files/constants/surfaceChrome";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { duplicateBoardSchema, DuplicateBoardFormData } from "@/react/schema/duplicateBoard.schema";
import { duplicateBoardAndTestDrive } from "@/network/network";
import { MessageDialog } from "@bryntum/schedulerpro";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import { extractAsyncTaskId } from "@/modern/components/async-tasks/utils";

const DUPLICATE_OPTIONS = [
  { key: "all", label: "All", description: "This is a great option for experimenting and training." },
  { key: "template", label: "Template", description: "Includes all status, category, tags, teams, type, and phase settings; icons; and saved views. This is the best option for creating a board template." },
  { key: "tasks", label: "Tasks", description: "Includes task general data, files, notes, budgets, checklists, and linked contacts." },
  { key: "resources", label: "Resources", description: "Includes resource general data, files, notes, checklists, and linked contacts." },
  { key: "clients", label: "Clients", description: "Includes client general data, files, notes, checklists, and linked contacts." },
  { key: "users", label: "Users", description: "Includes all board users, roles, permissions, and linked contacts." },
  { key: "contacts", label: "Contacts", description: "Includes all board contacts." }
] as const;

interface DuplicateBoardEditorContext {
  boardStore?: any;
  boardManager?: any;
  onComplete?: () => void;
  editorHandler?: any;
}

interface DuplicateBoardEditorProps {
  record: {
    record: {
      id: number;
      name?: string;
      display_name?: string;
      [key: string]: any;
    };
    context: DuplicateBoardEditorContext;
  };
  onClose: () => void;
  editorInstance?: any;
}

export const DuplicateBoardEditor = ({ record: { record, context }, onClose }: DuplicateBoardEditorProps) => {
  const boardName = record?.name || record?.display_name || "Board";
  const { pinTask } = useAsyncTaskContext();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitted }
  } = useForm<DuplicateBoardFormData>({
    resolver: zodResolver(duplicateBoardSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      boardName: "",
      all: false,
      template: false,
      tasks: false,
      resources: false,
      clients: false,
      users: false,
      contacts: false
    }
  });

  const watchAll = watch("all");
  const watchTemplate = watch("template");
  const watchTasks = watch("tasks");
  const watchResources = watch("resources");
  const watchClients = watch("clients");
  const watchUsers = watch("users");
  const watchContacts = watch("contacts");

  // Handle "All" checkbox logic
  useEffect(() => {
    if (watchAll) {
      const validate = { shouldValidate: isSubmitted } as const;
      setValue("template", true, validate);
      setValue("tasks", true, validate);
      setValue("resources", true, validate);
      setValue("clients", true, validate);
      setValue("users", true, validate);
      setValue("contacts", true, validate);
    }
  }, [watchAll, setValue, isSubmitted]);

  // Check if all individual options are selected to update "All"
  useEffect(() => {
    const allIndividualOptionsSelected =
      watchTemplate &&
      watchTasks &&
      watchResources &&
      watchClients &&
      watchUsers &&
      watchContacts;

    if (allIndividualOptionsSelected && !watchAll) {
      setValue("all", true, { shouldValidate: isSubmitted });
    } else if (!allIndividualOptionsSelected && watchAll) {
      setValue("all", false, { shouldValidate: isSubmitted });
    }
  }, [watchTemplate, watchTasks, watchResources, watchClients, watchUsers, watchContacts, watchAll, setValue, isSubmitted]);

  const onSubmit = (data: DuplicateBoardFormData) => {
    const boardDataCopyOptions = data.all
      ? ['everything']
      : Object.entries(data)
          .filter(([key, value]) => key !== 'boardName' && key !== 'all' && value === true)
          .map(([key]) => key);

    if (!boardDataCopyOptions.length) {
      MessageDialog.alert({
        title: "Duplicate Options Required",
        message: "At least one option must be selected when duplicating a board."
      });
      return;
    }

    duplicateBoardAndTestDrive(record.id, data.boardName, boardDataCopyOptions)
      .then((response) => {
        const taskId = extractAsyncTaskId(response.data);
        if (taskId == null) {
          MessageDialog.alert({
            title: "Duplication Failed",
            message: "The server did not return a task id. Please try again.",
          });
          return;
        }

        pinTask(taskId, {
          type: ASYNC_TASK_TYPES.BOARD_DUPLICATION,
          meta_data: {
            board_name: data.boardName,
            source_board_id: record.id,
          },
        });
        context?.onComplete?.();
        onClose();
      })
      .catch((error) => {
        MessageDialog.alert({
          title: "Duplication Failed",
          message: error?.response?.data?.detail || "Failed to duplicate board. Please try again."
        });
      });
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        dialogOverlayClassName="z-[--swivu-bryntum-z-index]"
        className={cn(
          "z-[--swivu-bryntum-z-index] flex h-[min(90vh,44rem)] max-h-[min(90vh,44rem)] w-[min(42rem,92vw)] max-w-[42rem] flex-col gap-0 overflow-hidden",
          "border-border/60 bg-background p-0 shadow-2xl sm:rounded-[2rem]",
        )}
      >
        <DialogTitle className="sr-only">Duplicate {boardName}</DialogTitle>
        <DialogDescription className="sr-only">
          Name the copy and choose what it includes.
        </DialogDescription>

        <div className="flex shrink-0 items-start gap-3 px-5 pt-5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-normal tracking-tight text-foreground sm:text-2xl">
              Duplicate {boardName}
            </h2>
            <p className="mt-0.5 text-sm font-normal text-muted-foreground">
              Name the copy and choose what it includes.
            </p>
          </div>
          <button
            type="button"
            className={cn(SURFACE_ICON_BTN_CLS, "inline-flex items-center justify-center")}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-5 pt-5 sm:px-7">
          <div className="flex shrink-0 flex-col gap-1.5">
            <Label htmlFor="boardName" className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
              Board Name
            </Label>
            <Input
              id="boardName"
              autoFocus
              aria-invalid={Boolean(errors.boardName)}
              aria-describedby={errors.boardName ? "boardName-error" : undefined}
              {...register("boardName")}
              placeholder={`Duplicating ${boardName}. Please enter a new name.`}
              className="h-9 rounded-md border-input bg-card px-3 text-sm text-foreground"
            />
            {errors.boardName && (
              <p id="boardName-error" className="text-sm text-destructive">
                {errors.boardName.message}
              </p>
            )}
          </div>

          <div className="mt-5 min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-2 pb-1">
              {DUPLICATE_OPTIONS.map((option) => {
                const checked = watch(option.key as keyof DuplicateBoardFormData) as boolean;
                return (
                  <label
                    key={option.key}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors",
                      checked
                        ? "border-primary/40 bg-primary/10"
                        : "border-border/50 hover:bg-muted/40",
                    )}
                  >
                    <Checkbox
                      id={option.key}
                      checked={checked}
                      onCheckedChange={(value) => {
                        setValue(option.key as keyof DuplicateBoardFormData, value === true, { shouldValidate: isSubmitted });
                      }}
                      className="mt-0.5"
                      aria-describedby={errors.all ? "duplicate-options-error" : undefined}
                    />
                    <span className="flex flex-1 flex-col gap-0.5">
                      <span className="text-sm font-normal leading-tight text-foreground">
                        {option.label}
                      </span>
                      <span className="text-sm leading-snug text-muted-foreground">
                        {option.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {errors.all ? (
          <p id="duplicate-options-error" className="shrink-0 px-5 pt-4 text-sm text-destructive sm:px-7">
            {errors.all.message}
          </p>
        ) : null}

        <div className={cn("flex shrink-0 items-center justify-end gap-2 px-5 pb-6 sm:px-7", errors.all ? "pt-2" : "pt-4")}>
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            <Copy className="size-4" />
            Duplicate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
